import { useState, FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { MeshBackground } from '../components/MeshBackground'
import { GlassPanel, GlassButton, GlassInput, GlassBadge } from '../components/ui/glass'
import { useAuth } from './useAuth'
import { cn } from '../lib/utils'

const ROLES = ['User', 'Builder', 'Trader'] as const
type SelectableRole = typeof ROLES[number]

export function SignupPage() {
  const { register, loginWithWallet, isLoading, error, clearError } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [role, setRole] = useState<SelectableRole>('User')
  const [confirmError, setConfirmError] = useState('')
  const [walletLoading, setWalletLoading] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    clearError()
    setConfirmError('')
    if (password !== confirm) { setConfirmError('Passwords do not match'); return }
    try {
      await register(email, password, role)
      navigate('/dashboard')
    } catch {}
  }

  const handleWalletConnect = async () => {
    clearError()
    setWalletLoading(true)
    try {
      await loginWithWallet()
      navigate('/dashboard')
    } catch {} finally {
      setWalletLoading(false)
    }
  }

  return (
    <div className="min-h-screen font-body text-white flex items-center justify-center p-4">
      <MeshBackground />
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-brand flex items-center justify-center shadow-glow-sky mb-4">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-display text-display-sm text-white">Create account</h1>
          <p className="text-white/50 text-sm mt-1">Join MEV Shield — protect your trades</p>
        </div>

        <GlassPanel gradient padding="lg">
          {(error || confirmError) && (
            <div className="mb-4">
              <GlassBadge variant="error">{error || confirmError}</GlassBadge>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <GlassInput type="email" label="Email" placeholder="you@example.com"
              value={email} onChange={e => setEmail(e.target.value)} required />
            <GlassInput type="password" label="Password" placeholder="••••••••"
              value={password} onChange={e => setPassword(e.target.value)} required />
            <GlassInput type="password" label="Confirm Password" placeholder="••••••••"
              value={confirm} onChange={e => setConfirm(e.target.value)} required />

            {/* Role Selector */}
            <div>
              <label className="block text-sm text-white/60 mb-2">I am a…</label>
              <div className="flex gap-2">
                {ROLES.map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    className={cn(
                      'flex-1 py-2 rounded-xl text-sm font-medium border transition-all duration-200',
                      role === r
                        ? 'bg-gradient-to-r from-primary to-accent text-white border-transparent shadow-glow-sky'
                        : 'bg-white/[0.05] border-white/10 text-white/60 hover:text-white hover:bg-white/[0.10]'
                    )}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <GlassButton type="submit" size="full" loading={isLoading}>
              Create Account →
            </GlassButton>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-white/30">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <GlassButton variant="secondary" size="full" onClick={handleWalletConnect} loading={walletLoading}>
            🦊 Register with MetaMask
          </GlassButton>

          <p className="text-center text-sm text-white/40 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:text-primary-light transition-colors">
              Sign in →
            </Link>
          </p>
        </GlassPanel>
      </div>
    </div>
  )
}
